import { useDisclosure } from '@mantine/hooks';
import { runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { TanStackTable, Toolbar } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import type { TCollectionTableSort } from '@feathr/extender/hooks';
import { useActiveListingsPolling, useCollectionTable } from '@feathr/extender/hooks';
import { useFlags, useStore } from '@feathr/extender/state';
import { isImportsRevampEnabled } from '@feathr/extender/utils/importsRevampFlags';

import AddImporterButton from './AddImporterButton';
import dataImportsColumns from './dataImportsColumns';
import { getImportsListFilters } from './DataImportsPage.utils';
import FilterImportsButton from './FilterImportsButton';
import { canExpandImportRow, renderImportRunDetails } from './ImportRunDetails';
import type { IImportsFilters } from './ImportsFilters';
import ImportsFiltersDrawer from './ImportsFiltersDrawer';

import styles from './DataImportsPage.css';

/** The legacy `<Table>`'s default page size; one of the sizes the pagination dropdown offers. */
const DEFAULT_PAGE_SIZE = 20;
/**
 * Newest import first. The MongoDB id encodes the creation time, so it sorts by date created.
 * Kept with the Finished on column too: sorting by `-date_completed` puts an import that has not
 * finished below every finished one, so a just-submitted import would fall off the first page.
 */
const INITIAL_SORT: TCollectionTableSort = [{ desc: true, id: 'id' }];

function DataImportsPage(): JSX.Element {
  const { Importers } = useStore();
  const isRevampEnabled = isImportsRevampEnabled(useFlags());
  const { t } = useTranslation();

  const [isFiltersDrawerOpen, { open: openFiltersDrawer, close: closeFiltersDrawer }] =
    useDisclosure(false);

  const state = useLocalObservable(() => ({
    filters: {} as IImportsFilters,
    appliedFilters: {} as IImportsFilters,
  }));

  function onApplyFilters(newFilters: IImportsFilters): void {
    runInAction(() => {
      state.filters = newFilters;
      state.appliedFilters = { ...newFilters };
    });
  }

  const tableFilters = getImportsListFilters(state.appliedFilters);

  // One paginated list request drives the table, its sort, and its loading state.
  const tableProps = useCollectionTable<Importer>(Importers, {
    defaultParams: { filters: tableFilters },
    initialPageSize: DEFAULT_PAGE_SIZE,
    initialSort: INITIAL_SORT,
  });

  // TanStack re-measures column widths when the array identity changes, so build it once per input.
  const columns = useMemo(() => dataImportsColumns(t, isRevampEnabled), [t, isRevampEnabled]);

  // `filter`, not `some`: reading every row re-renders the table, which updates `getRowCanExpand`.
  const activeImports = tableProps.items.filter((importer) =>
    Importers.isActiveForRefresh(importer),
  );
  useActiveListingsPolling(Importers, isRevampEnabled && activeImports.length > 0);

  const actions = (
    <Toolbar>
      <AddImporterButton />
    </Toolbar>
  );

  return (
    <Page
      actions={actions}
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={t(
        'Start new contact data imports or manage existing ones. This is also where you can import lists of unsubscribed contacts.',
      )}
      title={t('Imports')}
    >
      <Toolbar align={'right'} className={styles.filtersToolbar}>
        <FilterImportsButton onClick={openFiltersDrawer} />
      </Toolbar>
      <TanStackTable<Importer>
        {...tableProps}
        columns={columns}
        getRowCanExpand={canExpandImportRow}
        // One panel at a time: the panel is a detail view, and stacked panels push the rows apart.
        isSingleRowExpansion={true}
        noDataText={t('No imports found.')}
        renderSubComponent={isRevampEnabled ? renderImportRunDetails : undefined}
      />
      <ImportsFiltersDrawer
        close={closeFiltersDrawer}
        filters={state.filters}
        isOpen={isFiltersDrawerOpen}
        onApply={onApplyFilters}
      />
    </Page>
  );
}

export default observer(DataImportsPage);
